/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState, useEffect, useCallback } from 'react'
import { api } from '@/lib/api'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Activity,
  Cpu,
  HardDrive,
  Database,
  Trash2,
  RefreshCw,
  Clock,
  Server,
  MemoryStick,
  Layers,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface SystemStatusData {
  version: string
  start_time: string
  system_name: string
}

interface PerformanceStats {
  cache_stats: CacheStats
  memory_stats: MemoryStats
  disk_cache_info: DiskCacheInfo
  disk_space_info: DiskSpaceInfo
  config: PerformanceConfig
}

interface CacheStats {
  hit_count: number
  miss_count: number
  write_count: number
}

interface MemoryStats {
  alloc: number
  total_alloc: number
  sys: number
  num_gc: number
  num_goroutine: number
}

interface DiskCacheInfo {
  path: string
  exists: boolean
  file_count: number
  total_size: number
}

interface DiskSpaceInfo {
  total: number
  used: number
  free: number
  used_percent: number
}

interface PerformanceConfig {
  disk_cache_enabled: boolean
  disk_cache_threshold_mb: number
  disk_cache_max_size_mb: number
  disk_cache_path: string
  is_running_in_container: boolean
  monitor_enabled: boolean
  monitor_cpu_threshold: number
  monitor_memory_threshold: number
  monitor_disk_threshold: number
}

interface AffinityCacheData {
  [key: string]: any
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${units[i]}`
}

function formatUptime(startTime: string): string {
  if (!startTime) return '-'
  try {
    const start = new Date(startTime).getTime()
    if (isNaN(start)) return '-'
    const diff = Date.now() - start
    const days = Math.floor(diff / 86400000)
    const hours = Math.floor((diff % 86400000) / 3600000)
    const mins = Math.floor((diff % 3600000) / 60000)
    if (days > 0) return `${days}d ${hours}h ${mins}m`
    if (hours > 0) return `${hours}h ${mins}m`
    return `${mins}m`
  } catch {
    return '-'
  }
}

function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
  color = 'text-foreground',
}: {
  icon: React.ElementType
  label: string
  value: string | React.ReactNode
  subValue?: string
  color?: string
}) {
  return (
    <Card className="p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="size-4" />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
      {subValue && <div className="text-xs text-muted-foreground">{subValue}</div>}
    </Card>
  )
}

export function SystemStatus() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<SystemStatusData | null>(null)
  const [perfStats, setPerfStats] = useState<PerformanceStats | null>(null)
  const [affinityStats, setAffinityStats] = useState<AffinityCacheData | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [statusRes, perfRes, affinityRes] = await Promise.allSettled([
        api.get('/api/status'),
        api.get('/api/performance/stats'),
        api.get('/api/option/channel_affinity_cache'),
      ])

      if (statusRes.status === 'fulfilled') {
        setStatus(statusRes.value.data?.data || null)
      }
      if (perfRes.status === 'fulfilled') {
        setPerfStats(perfRes.value.data?.data || null)
      }
      if (affinityRes.status === 'fulfilled') {
        setAffinityStats(affinityRes.value.data?.data || null)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleAction = async (action: string, url: string) => {
    setActionLoading(action)
    setActionResult(null)
    try {
      const res = await api.post(url)
      setActionResult({ type: 'success', msg: res.data?.message || t('Operation completed') })
      fetchData()
    } catch (e: any) {
      setActionResult({ type: 'error', msg: e.response?.data?.message || e.message || t('Operation failed') })
    } finally {
      setActionLoading(action)
      setTimeout(() => setActionLoading(null), 2000)
    }
  }

  const uptime = status ? formatUptime(status.start_time) : '-'

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-6 py-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Activity className="size-5" />
          {t('System Status')}
        </h1>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`size-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {t('Refresh')}
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {actionResult && (
          <div
            className={`mb-4 p-3 rounded-md text-sm flex items-center gap-2 ${
              actionResult.type === 'success' ? 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400'
            }`}
          >
            {actionResult.type === 'success' ? <CheckCircle2 className="size-4 shrink-0" /> : <AlertCircle className="size-4 shrink-0" />}
            {actionResult.msg}
          </div>
        )}

        <div className="space-y-6 max-w-6xl mx-auto">
          {/* Server Info */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Server className="size-4" />
              {t('Server Information')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Server}
                label={t('Version')}
                value={status?.version || '-'}
              />
              <StatCard
                icon={Clock}
                label={t('Uptime')}
                value={uptime}
              />
              <StatCard
                icon={Activity}
                label={t('System Name')}
                value={status?.system_name || '-'}
              />
              <StatCard
                icon={Layers}
                label={t('Runtime Environment')}
                value={
                  perfStats?.config?.is_running_in_container ? t('Container') : t('Native')
                }
              />
            </div>
          </section>

          {/* Memory Stats */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <MemoryStick className="size-4" />
              {t('Memory Usage')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={MemoryStick}
                label={t('Allocated Memory')}
                value={perfStats ? formatBytes(perfStats.memory_stats.alloc) : '-'}
              />
              <StatCard
                icon={Cpu}
                label={t('Total Allocated')}
                value={perfStats ? formatBytes(perfStats.memory_stats.total_alloc) : '-'}
              />
              <StatCard
                icon={Database}
                label={t('System Memory')}
                value={perfStats ? formatBytes(perfStats.memory_stats.sys) : '-'}
              />
              <StatCard
                icon={Activity}
                label={t('Goroutines')}
                value={perfStats ? String(perfStats.memory_stats.num_goroutine) : '-'}
                subValue={perfStats ? `GC: ${perfStats.memory_stats.num_gc}` : undefined}
              />
            </div>
          </section>

          {/* Disk Space */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <HardDrive className="size-4" />
              {t('Disk Space')}
            </h2>
            <Card className="p-4">
              {!perfStats?.disk_space_info ? (
                <p className="text-sm text-muted-foreground">{t('No data available')}</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('Total')}</p>
                    <p className="text-lg font-semibold mt-1">{formatBytes(perfStats.disk_space_info.total)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('Used')}</p>
                    <p className="text-lg font-semibold mt-1">{formatBytes(perfStats.disk_space_info.used)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('Free')}</p>
                    <p className="text-lg font-semibold mt-1">{formatBytes(perfStats.disk_space_info.free)}</p>
                  </div>
                </div>
              )}
              {perfStats?.disk_space_info && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>{t('Disk Usage')}</span>
                    <span>{perfStats.disk_space_info.used_percent.toFixed(1)}%</span>
                  </div>
                  <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        perfStats.disk_space_info.used_percent > 90
                          ? 'bg-red-500'
                          : perfStats.disk_space_info.used_percent > 70
                            ? 'bg-yellow-500'
                            : 'bg-primary'
                      }`}
                      style={{ width: `${Math.min(perfStats.disk_space_info.used_percent, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </Card>
          </section>

          {/* Disk Cache */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Database className="size-4" />
              {t('Disk Cache')}
            </h2>
            <Card className="p-4">
              {!perfStats?.disk_cache_info ? (
                <p className="text-sm text-muted-foreground">{t('No data available')}</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Status')}</p>
                      <p className="text-sm font-medium mt-1">
                        {perfStats.config.disk_cache_enabled ? (
                          <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="size-3" />{t('Enabled')}</span>
                        ) : (
                          <span className="text-muted-foreground">{t('Disabled')}</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Files')}</p>
                      <p className="text-sm font-medium mt-1">{String(perfStats.disk_cache_info.file_count)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Size')}</p>
                      <p className="text-sm font-medium mt-1">{formatBytes(perfStats.disk_cache_info.total_size)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Max Size')}</p>
                      <p className="text-sm font-medium mt-1">{perfStats.config.disk_cache_max_size_mb > 0 ? `${perfStats.config.disk_cache_max_size_mb} MB` : '-'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-3 border-t">
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Cache Hits')}</p>
                      <p className="text-sm font-medium mt-1">{String(perfStats.cache_stats.hit_count)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Cache Misses')}</p>
                      <p className="text-sm font-medium mt-1">{String(perfStats.cache_stats.miss_count)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Cache Writes')}</p>
                      <p className="text-sm font-medium mt-1">{String(perfStats.cache_stats.write_count)}</p>
                    </div>
                  </div>
                </>
              )}

              <div className="mt-4 pt-3 border-t flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={actionLoading !== null}
                  onClick={() => handleAction('clear-disk-cache', '/api/performance/disk_cache')}
                >
                  <Trash2 className="size-3 mr-1" />
                  {actionLoading === 'clear-disk-cache' ? t('Processing...') : t('Clear Inactive Cache')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={actionLoading !== null}
                  onClick={() => handleAction('force-gc', '/api/performance/gc')}
                >
                  <RefreshCw className="size-3 mr-1" />
                  {actionLoading === 'force-gc' ? t('Processing...') : t('Force GC')}
                </Button>
              </div>
            </Card>
          </section>

          {/* Channel Affinity Cache */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Layers className="size-4" />
              {t('Channel Affinity Cache')}
            </h2>
            <Card className="p-4">
              {!affinityStats ? (
                <p className="text-sm text-muted-foreground">{t('No data available')}</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {Object.entries(affinityStats).map(([key, val]: [string, any]) => (
                    <div key={key} className="p-2 rounded bg-muted/50">
                      <p className="text-xs text-muted-foreground truncate" title={key}>{key}</p>
                      <p className="text-sm font-medium mt-1">
                        {typeof val === 'object' && val !== null
                          ? Object.keys(val).length + ' entries'
                          : String(val)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-3 border-t flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={actionLoading !== null}
                  onClick={() => handleAction('clear-affinity', '/api/option/channel_affinity_cache?all=true')}
                >
                  <Trash2 className="size-3 mr-1" />
                  {actionLoading === 'clear-affinity' ? t('Processing...') : t('Clear All Affinity Cache')}
                </Button>
              </div>
            </Card>
          </section>
        </div>
      </div>
    </div>
  )
}